import { gql, MutationHookOptions, QueryHookOptions, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
  Mutation,
  MutationToggleRebateCardStarArgs,
  MutationToggleRebateCardVisibilityArgs,
  MutationUpdateRebateCardNoteArgs,
  Query,
  QueryRebateComplianceScorecardsArgs,
  QueryRebateComplianceScorecardStatsArgs,
  QueryRebateOpportunityCardsArgs,
  QueryRebateOpportunityStatsArgs,
} from '../gen/models'

const REBATE_OPPORTUNITY_CARDS_FILTER_TERMS_QUERY = gql`
  query rebateOpportunityCardsFilterTerms {
    rebateOpportunityCardsFilterTerms {
      suppliers
      contractCategories
      curvoCategories
      rebateTypes
      topParents {
        id
        name
      }
    }
  }
`

export function useRebateOpportunityCardsFilterTerms() {
  return useQuery<Pick<Query, 'rebateOpportunityCardsFilterTerms'>>(REBATE_OPPORTUNITY_CARDS_FILTER_TERMS_QUERY)
}

const REBATE_OPPORTUNITY_CARDS_QUERY = gql`
  query rebateOpportunityCards($input: RebateOpportunityQueryInput!) {
    rebateOpportunityCards(input: $input) {
      hits {
        contractId
        rebateId
        tierId
        rebatePeriodOffset
        startDate
        endDate
        facilityId
        topParentId
        customerId
        facility
        topParent
        customer
        contractInfo {
          name
          code
          supplier
          category
          description
        }
        rebateInfo {
          name
          type
          payoutLevel
        }
        mostImpactRuleStat {
          ruleId
          ruleType
          metricType
          valueToValidate
          operator
          periodType
          valueFirst
          valueSecond
          closeness
          likelihoodStatus
          likelihoodScore
        }
        rebateTiers {
          tierIndex
          tierLabel
          rebateId
          value
          isSelectedTier
          valid
          closeness
          totalRebate
          likelihoodStatus
          ruleHumanReadable
        }
        eligibleProjectedSpend
        eligibleUnits
        likelihoodScore
        numberOfRules
        totalRebate
        isStarred
        isHidden
        note
        snoozeUntil
      }
      totalHits
    }
  }
`

const REBATE_OPPORTUNITY_CHARTS_QUERY = gql`
  query rebateOpportunityCharts($input: RebateOpportunityQueryInput!) {
    rebateOpportunityCards(input: $input) {
      hits {
        facility
        topParent
        customer
        contractInfo {
          code
          supplier
        }
        rebateInfo {
          name
        }
        mostImpactRuleStat {
          valueToValidate
          valueFirst
          ruleType
          likelihoodStatus
        }
        endDate
        totalRebate
        likelihoodScore
      }
      totalHits
    }
  }
`

const TOGGLE_REBATE_CARD_STAR_MUTATION = gql`
  mutation toggleRebateCardStar($input: RebateCardPreferenceInput!, $starred: Boolean!, $note: String) {
    toggleRebateCardStar(input: $input, starred: $starred, note: $note) {
      id
      isStarred
      isHidden
      note
      snoozeUntil
    }
  }
`

const TOGGLE_REBATE_CARD_VISIBILITY_MUTATION = gql`
  mutation toggleRebateCardVisibility(
    $input: RebateCardPreferenceInput!
    $hidden: Boolean!
    $note: String
    $snoozeDays: Int
  ) {
    toggleRebateCardVisibility(input: $input, hidden: $hidden, note: $note, snoozeDays: $snoozeDays) {
      id
      isStarred
      isHidden
      note
      snoozeUntil
    }
  }
`

const UPDATE_REBATE_CARD_NOTE_MUTATION = gql`
  mutation updateRebateCardNote($input: RebateCardPreferenceInput!, $note: String) {
    updateRebateCardNote(input: $input, note: $note) {
      id
      isStarred
      isHidden
      note
      snoozeUntil
    }
  }
`

export function useToggleRebateCardStar(
  options?: MutationHookOptions<Pick<Mutation, 'toggleRebateCardStar'>, MutationToggleRebateCardStarArgs>,
) {
  return useMutation<Pick<Mutation, 'toggleRebateCardStar'>, MutationToggleRebateCardStarArgs>(
    TOGGLE_REBATE_CARD_STAR_MUTATION,
    options,
  )
}

export function useToggleRebateCardVisibility(
  options?: MutationHookOptions<Pick<Mutation, 'toggleRebateCardVisibility'>, MutationToggleRebateCardVisibilityArgs>,
) {
  return useMutation<Pick<Mutation, 'toggleRebateCardVisibility'>, MutationToggleRebateCardVisibilityArgs>(
    TOGGLE_REBATE_CARD_VISIBILITY_MUTATION,
    options,
  )
}

export function useUpdateRebateCardNote(
  options?: MutationHookOptions<Pick<Mutation, 'updateRebateCardNote'>, MutationUpdateRebateCardNoteArgs>,
) {
  return useMutation<Pick<Mutation, 'updateRebateCardNote'>, MutationUpdateRebateCardNoteArgs>(
    UPDATE_REBATE_CARD_NOTE_MUTATION,
    options,
  )
}

export function useRebateOpportunitiesCards(
  options: QueryHookOptions<Pick<Query, 'rebateOpportunityCards'>, QueryRebateOpportunityCardsArgs>,
) {
  return useQuery<Pick<Query, 'rebateOpportunityCards'>, QueryRebateOpportunityCardsArgs>(
    REBATE_OPPORTUNITY_CARDS_QUERY,
    options,
  )
}

const REBATE_OPPORTUNITY_STATS_QUERY = gql`
  query rebateOpportunityStats($input: RebateOpportunityStatsInput!) {
    rebateOpportunityStats(input: $input) {
      totalCount
      increaseVolumeTotalRebate
      vendorShiftTotalRebate
      onTrackTotalRebate
      lockedInTotalRebate
      openTotalRebate
    }
  }
`

export function useRebateOpportunityStats(
  options: QueryHookOptions<Pick<Query, 'rebateOpportunityStats'>, QueryRebateOpportunityStatsArgs>,
) {
  return useQuery<Pick<Query, 'rebateOpportunityStats'>, QueryRebateOpportunityStatsArgs>(
    REBATE_OPPORTUNITY_STATS_QUERY,
    options,
  )
}

export function useRebateOpportunitiesCharts(
  options: QueryHookOptions<Pick<Query, 'rebateOpportunityCards'>, QueryRebateOpportunityCardsArgs>,
) {
  return useQuery<Pick<Query, 'rebateOpportunityCards'>, QueryRebateOpportunityCardsArgs>(
    REBATE_OPPORTUNITY_CHARTS_QUERY,
    options,
  )
}

const REBATE_COMPLIANCE_SCORECARD_QUERY = gql`
  query rebateComplianceScorecards($input: RebateComplianceScorecardInput!) {
    rebateComplianceScorecards(input: $input) {
      hits {
        contractId
        rebateId
        tierId
        rebatePeriodOffset
        startDate
        endDate
        facility
        facilityId
        topParent
        topParentId
        customer
        rebateScope
        contractInfo {
          name
          code
          supplier
          category
        }
        rebateInfo {
          name
          type
          payoutLevel
          metricType
          value
          baselineSpend
        }
        rebateGroupName
        rebateTierIndex
        rebateTiers {
          tierIndex
          tierLabel
          rebateId
          metricType
          value
          isSelectedTier
          valid
          closeness
          totalRebate
          likelihoodStatus
          riskStatus
          ruleHumanReadable
          ruleStats {
            operator
            metricType
            valueFirst
            valueSecond
          }
        }
        riskStatus
        closeness
        mostImpactRuleStat {
          ruleType
          metricType
          valueToValidate
          valueFirst
          closeness
          likelihoodStatus
        }
        ruleStatuses {
          ruleId
          ruleType
          ruleHumanReadable
          metricType
          periodType
          entity
          valueToValidate
          valueFirst
          closeness
          likelihoodStatus
          startDate
          endDate
        }
        totalRebate
      }
      totalHits
    }
  }
`

export function useRebateScorecardCards(
  options: QueryHookOptions<Pick<Query, 'rebateComplianceScorecards'>, QueryRebateComplianceScorecardsArgs>,
) {
  return useQuery<Pick<Query, 'rebateComplianceScorecards'>, QueryRebateComplianceScorecardsArgs>(
    REBATE_COMPLIANCE_SCORECARD_QUERY,
    options,
  )
}

export function useRebateScorecardCardsLazy() {
  return useLazyQuery<Pick<Query, 'rebateComplianceScorecards'>, QueryRebateComplianceScorecardsArgs>(
    REBATE_COMPLIANCE_SCORECARD_QUERY,
  )
}

const REBATE_COMPLIANCE_SCORECARD_FILTER_TERMS_QUERY = gql`
  query rebateComplianceScorecardFilterTerms {
    rebateComplianceScorecardFilterTerms {
      suppliers
      contractCodes {
        id
        code
      }
      rebateTypes
      topParents {
        id
        name
      }
      facilities {
        facilityId
        name
      }
    }
  }
`

export function useRebateComplianceScorecardFilterTerms() {
  return useQuery<Pick<Query, 'rebateComplianceScorecardFilterTerms'>>(REBATE_COMPLIANCE_SCORECARD_FILTER_TERMS_QUERY)
}

const REBATE_COMPLIANCE_SCORECARD_STATS_QUERY = gql`
  query rebateComplianceScorecardStats($input: RebateComplianceScorecardStatsInput!) {
    rebateComplianceScorecardStats(input: $input) {
      contractCount
      rebateCount
      atRiskCount
      earnedCount
      ontrackCount
      missedCount
      atRiskTotalRebate
      ontrackTotalRebate
      earnedTotalRebate
      missedTotalRebate
    }
  }
`

export function useRebateComplianceScorecardStats(
  options: QueryHookOptions<Pick<Query, 'rebateComplianceScorecardStats'>, QueryRebateComplianceScorecardStatsArgs>,
) {
  return useQuery<Pick<Query, 'rebateComplianceScorecardStats'>, QueryRebateComplianceScorecardStatsArgs>(
    REBATE_COMPLIANCE_SCORECARD_STATS_QUERY,
    options,
  )
}

const REBATE_RECONCILIATION_STATS_QUERY = gql`
  query rebateReconciliationStats($input: RebateReconciliationStatsInput!) {
    rebateReconciliationStats(input: $input) {
      totalEarned
      totalActualReceived
      pendingCollectionCount
      discrepancyCount
      avgDTC
      collectionStatusDistribution {
        collectionStatus
        earned
      }
      collectionStatusMonthly {
        date
        collected
        earned
      }
      collectionStatusBySupplier {
        supplier
        collected
        earned
      }
    }
  }
`

export function useRebateReconciliationStats(options: QueryHookOptions<Pick<Query, 'rebateReconciliationStats'>, any>) {
  return useQuery<Pick<Query, 'rebateReconciliationStats'>, any>(REBATE_RECONCILIATION_STATS_QUERY, options)
}
